<template>
  <div class="stage" :class="{ '--has-overlay': dialogOpen }">
    <aside class="stage-rail">
      <button
        ref="trigger"
        class="stage-add-slide"
        type="button"
        data-testid="add-slide-trigger"
        @click="toggleDialog"
      >
        <span class="material-symbols-rounded">add</span>
        <span>Add slide</span>
      </button>
      <div class="stage-thumbs">
        <div class="stage-slide-thumb stage-slide-thumb--active">
          1
        </div>
        <div class="stage-slide-thumb">
          2
        </div>
        <div class="stage-slide-thumb">
          3
        </div>
        <div class="stage-slide-thumb">
          4
        </div>
        <div class="stage-slide-thumb">
          5
        </div>
        <div class="stage-slide-thumb">
          6
        </div>
        <div class="stage-slide-thumb">
          7
        </div>
      </div>
    </aside>

    <main class="stage-main">
      <header class="stage-topbar">
        <span class="stage-topbar-title">DSN-798 · Slide Picker design demo</span>
        <span class="stage-topbar-meta">
          <span class="material-symbols-rounded filled stage-topbar-heart">bookmark</span>
          My Bookmarks: {{ pickerCount }} · All slides: {{ totalCount }}
        </span>
      </header>
      <section class="stage-canvas">
        <div class="stage-empty">
          <span class="material-symbols-rounded">slideshow</span>
          <p>This stage simulates the editor. Click <strong>Add slide</strong> to open the slide picker.</p>
          <p class="stage-empty-hint">
            Two tabs: <strong>My Bookmarks</strong> for your saved slides, <strong>Marketplace</strong> to browse everything by category.
          </p>
        </div>
      </section>
    </main>

    <transition name="backdrop">
      <div
        v-if="dialogOpen"
        class="overlay-backdrop"
        :class="{ '--strong': isExpanded }"
        data-testid="dialog-backdrop"
        @click="closeAll"
      />
    </transition>

    <!-- Dialog: two surfaces side by side — My Bookmarks (personal,
         minimal) and Marketplace (categories + search + banner +
         grid). A segmented tab control in the header switches between
         them; chrome (search, banner, sidebar) only appears in
         marketplace mode. -->
    <div
      v-if="dialogOpen"
      ref="dialog"
      v-click-outside-dialog
      class="dialog --expanded"
      :class="{ '--mobile': isMobile }"
      :style="dialogStyle"
      @click.stop
    >
      <header class="dialog-header">
        <!-- Back pill only appears on marketplace detail page;
             clicking it returns the user to the marketplace grid. -->
        <button
          type="button"
          class="dialog-back --market"
          :class="{ '--hidden': !showBackPill }"
          :aria-hidden="!showBackPill"
          :tabindex="showBackPill ? 0 : -1"
          data-testid="dialog-back-to-marketplace"
          @click="onDialogBack"
        >
          <span class="dialog-back-inner">
            <span class="material-symbols-rounded">arrow_back</span>
            <span class="dialog-back-label">Marketplace</span>
          </span>
        </button>
        <div class="dialog-modes" role="tablist" aria-label="Slide picker surface">
          <button
            type="button"
            role="tab"
            class="dialog-mode"
            :class="{ '--active': mode === 'bookmarks' }"
            :aria-selected="mode === 'bookmarks'"
            data-testid="dialog-mode-bookmarks"
            @click="onModeChange('bookmarks')"
          >
            <span
              class="material-symbols-rounded"
              :class="{ filled: mode === 'bookmarks' }"
            >bookmark</span>
            <span>My Bookmarks</span>
            <span v-if="bookmarkCount > 0" class="dialog-mode-count">{{ bookmarkCount }}</span>
          </button>
          <button
            type="button"
            role="tab"
            class="dialog-mode"
            :class="{ '--active': mode === 'marketplace' }"
            :aria-selected="mode === 'marketplace'"
            data-testid="dialog-mode-marketplace"
            @click="onModeChange('marketplace')"
          >
            <span class="material-symbols-rounded">storefront</span>
            <span>Marketplace</span>
          </button>
        </div>
        <div v-if="mode === 'marketplace'" class="dialog-search">
          <span class="material-symbols-rounded dialog-search-icon">search</span>
          <input
            ref="searchInput"
            v-model="searchQuery"
            type="text"
            class="dialog-search-input"
            :placeholder="searchPlaceholder"
            spellcheck="false"
            autocomplete="off"
            data-testid="dialog-search"
          />
          <button
            v-if="searchQuery"
            type="button"
            class="dialog-search-clear"
            aria-label="Clear search"
            @click.stop="searchQuery = ''"
          >
            <span class="material-symbols-rounded">close</span>
          </button>
        </div>
        <button
          class="dialog-close"
          type="button"
          aria-label="Close"
          data-testid="dialog-close"
          @click="closeAll"
        >
          <span class="material-symbols-rounded">close</span>
        </button>
      </header>
      <div class="dialog-body">
        <div class="dialog-pane">
          <SlideBookmarksPane
            v-if="mode === 'bookmarks'"
            @select="onPickFromMarketplace"
            @go-marketplace="onModeChange('marketplace')"
          />
          <SlideMarketplace
            v-else
            ref="marketplace"
            :search-query="searchQuery"
            :initial-slide-id="initialSlideId"
            @select="onPickFromMarketplace"
            @view-change="onMarketplaceViewChange"
            @clear-search="searchQuery = ''"
          />
        </div>
      </div>
    </div>

    <transition name="confirm">
      <div
        v-if="confirmRequest"
        class="confirm-overlay"
        data-testid="remove-confirm-overlay"
        @click.self="onConfirmCancel"
        @keydown.esc.stop.prevent="onConfirmCancel"
      >
        <div class="confirm-dialog" role="alertdialog" aria-labelledby="confirm-title">
          <span class="confirm-icon material-symbols-rounded filled">bookmark_remove</span>
          <h3 id="confirm-title" class="confirm-title">
            Remove “{{ confirmRequest.slideName }}” from My Bookmarks?
          </h3>
          <p class="confirm-body">
            Don’t worry — you can add it back any time from the marketplace.
          </p>
          <div class="confirm-actions">
            <button
              type="button"
              class="confirm-btn confirm-btn--ghost"
              data-testid="remove-confirm-cancel"
              @click="onConfirmCancel"
            >
              Cancel
            </button>
            <button
              ref="confirmPrimary"
              type="button"
              class="confirm-btn confirm-btn--danger"
              data-testid="remove-confirm-ok"
              @click="onConfirmOk"
            >
              Yes, remove
            </button>
          </div>
        </div>
      </div>
    </transition>

    <transition name="toast">
      <div v-if="toast" class="toast" :key="toast.key">
        <span class="material-symbols-rounded filled" :style="toast.iconColor ? { color: toast.iconColor } : null">{{ toast.icon }}</span>
        <span>
          {{ toast.before }}
          <strong>{{ toast.emphasis }}</strong>
          {{ toast.after }}
        </span>
      </div>
    </transition>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import SlideMarketplace from './components/SlideMarketplace.vue'
import SlideBookmarksPane from './components/SlideBookmarksPane.vue'
import { isInPicker } from './lib/pinnedStore'
import { confirmStore, resolveConfirm } from './lib/confirmStore'
import { toastStore, showToast, clearToast } from './lib/toastStore'
import { SLIDE_CATALOG, type SlideType } from './data/slideTypeCatalog'

type Mode = 'bookmarks' | 'marketplace'

const MODE_KEY = 'aha:slide-picker-demo:mode'
const DEFAULT_MODE: Mode = 'marketplace'

function loadMode(): Mode {
  try {
    const raw = localStorage.getItem(MODE_KEY)
    if (raw === 'bookmarks' || raw === 'marketplace') return raw
  } catch { /* private mode etc. */ }
  return DEFAULT_MODE
}

function saveMode(mode: Mode): void {
  try {
    localStorage.setItem(MODE_KEY, mode)
  } catch { /* private mode etc. */ }
}

const MARKET_W = 1200
const MARKET_W_MIN = 520
const EXPANDED_H = 820
const VIEWPORT_MARGIN = 16
const MOBILE_BREAKPOINT = 768
const MOBILE_MARGIN = 0
const TOAST_DURATION_MS = 2200

export default Vue.extend({
  name: 'SlidePickerDemoApp',
  components: { SlideMarketplace, SlideBookmarksPane },
  directives: {
    'click-outside-dialog': {
      bind(el, _binding, vnode) {
        (el as any).__handler = (event: MouseEvent) => {
          if (el.contains(event.target as Node)) return
          const trigger = (vnode.context as any)?.$refs?.trigger as HTMLElement | undefined
          if (trigger && trigger.contains(event.target as Node)) return
          if (confirmStore.pending) return;
          (vnode.context as any)?.closeAll?.()
        }
        document.addEventListener('mousedown', (el as any).__handler, true)
      },
      unbind(el) {
        document.removeEventListener('mousedown', (el as any).__handler, true)
      },
    },
  },
  data() {
    return {
      dialogOpen: false,
      viewportW: typeof window !== 'undefined' ? window.innerWidth : 1280,
      viewportH: typeof window !== 'undefined' ? window.innerHeight : 800,
      toastClearTimer: null as number | null,
      totalCount: SLIDE_CATALOG.length,
      initialSlideId: null as string | null,
      searchQuery: '',
      // Top-level dialog surface — 'bookmarks' or 'marketplace'.
      // Marketplace is the first-time default; once the user switches
      // the choice is persisted to localStorage.
      mode: loadMode(),
      // Mirrored from SlideMarketplace so the dialog header knows
      // whether to surface the "← Marketplace" back pill (detail page
      // only).
      marketplaceView: 'home' as 'home' | 'detail',
    }
  },
  computed: {
    isMobile(): boolean {
      return this.viewportW <= MOBILE_BREAKPOINT
    },
    marketWidth(): number {
      const maxW = this.viewportW - VIEWPORT_MARGIN * 2
      return Math.max(MARKET_W_MIN, Math.min(MARKET_W, maxW))
    },
    dialogStyle(): Record<string, string> {
      if (this.isMobile) {
        return {
          left: `${MOBILE_MARGIN}px`,
          top: `${MOBILE_MARGIN}px`,
          width: `${this.viewportW - MOBILE_MARGIN * 2}px`,
          height: `${this.viewportH - MOBILE_MARGIN * 2}px`,
        }
      }
      const margin = VIEWPORT_MARGIN
      const maxW = this.viewportW - margin * 2
      const maxH = this.viewportH - margin * 2
      const totalW = Math.min(this.marketWidth, maxW)
      const totalH = Math.min(EXPANDED_H, maxH)
      const left = Math.round((this.viewportW - totalW) / 2)
      const top = Math.round((this.viewportH - totalH) / 2)
      return {
        left: `${left}px`,
        top: `${top}px`,
        width: `${totalW}px`,
        height: `${totalH}px`,
      }
    },
    confirmRequest(): { slideName: string } | null {
      return confirmStore.pending
    },
    toast() {
      return toastStore.current
    },
    pickerCount(): number {
      return SLIDE_CATALOG.filter(isInPicker).length
    },
    bookmarkCount(): number {
      return this.pickerCount
    },
    searchPlaceholder(): string {
      return `Search ${this.totalCount} slides — try “icebreaker”…`
    },
    showBackPill(): boolean {
      return this.mode === 'marketplace' && this.marketplaceView === 'detail'
    },
  },
  watch: {
    confirmRequest(newVal) {
      if (!newVal) return
      this.$nextTick(() => {
        const btn = this.$refs.confirmPrimary as HTMLButtonElement | undefined
        btn?.focus()
      })
    },
    toast(newToast) {
      // Auto-clear after a few seconds so the toast doesn't linger.
      if (this.toastClearTimer) window.clearTimeout(this.toastClearTimer)
      if (!newToast) return
      this.toastClearTimer = window.setTimeout(() => {
        clearToast()
      }, TOAST_DURATION_MS)
    },
  },
  mounted() {
    document.addEventListener('keydown', this.handleKey)
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKey)
    window.removeEventListener('resize', this.handleResize)
    if (this.toastClearTimer) window.clearTimeout(this.toastClearTimer)
  },
  methods: {
    handleKey(e: KeyboardEvent) {
      if (e.key !== 'Escape') return
      if (this.confirmRequest) {
        e.stopImmediatePropagation()
        e.preventDefault()
        this.onConfirmCancel()
        return
      }
      if (this.dialogOpen) this.closeAll()
    },
    onConfirmOk() { resolveConfirm(true) },
    onConfirmCancel() { resolveConfirm(false) },
    handleResize() {
      this.viewportW = window.innerWidth
      this.viewportH = window.innerHeight
    },
    toggleDialog() {
      if (this.dialogOpen) {
        this.closeAll()
        return
      }
      this.openPicker()
    },
    openPicker() {
      this.dialogOpen = true
      this.searchQuery = ''
      this.initialSlideId = null
      this.marketplaceView = 'home'
    },
    onModeChange(mode: Mode) {
      if (this.mode === mode) return
      this.mode = mode
      saveMode(mode)
      // Search lives on the marketplace surface only; clearing on
      // every switch keeps a stale query from carrying over.
      this.searchQuery = ''
      // Bookmarks pane has no detail view, so reset the marketplace's
      // detail-tracker so the back-pill resolves correctly on return.
      if (mode === 'bookmarks') this.marketplaceView = 'home'
    },
    onMarketplaceViewChange(view: 'home' | 'detail') {
      this.marketplaceView = view
    },
    onDialogBack() {
      if (this.mode === 'marketplace' && this.marketplaceView === 'detail') {
        (this.$refs.marketplace as any)?.goHome?.()
      }
    },
    closeAll() {
      this.dialogOpen = false
      this.initialSlideId = null
      this.searchQuery = ''
    },
    onPickFromMarketplace(slide: SlideType) {
      this.announceDeckAdd(slide)
      this.closeAll()
    },
    announceDeckAdd(slide: SlideType) {
      showToast({
        before: 'Added',
        emphasis: slide.name,
        after: 'to deck',
        icon: slide.icon,
        iconColor: slide.accent,
      })
    },
  },
})
</script>

<style lang="scss">
:root {
  --ease-out: cubic-bezier(0.23, 1, 0.32, 1);
  --ease-drawer: cubic-bezier(0.32, 0.72, 0, 1);

  --aha-pink: #FF4081;
  --aha-purple: #6A1EBB;
  --aha-purple-deep: #5A189A;
  --aha-text: #1A1A2E;
  --aha-error: #E6005C;
  --aha-lavender: #D3B4FF;
  --aha-blush: #FDF6FA;
  --aha-sky: #F0F4FF;

  --aha-purple-tint: rgba(106, 30, 187, 0.08);
  --aha-purple-tint-2: rgba(106, 30, 187, 0.14);
  --aha-pink-tint: rgba(255, 64, 129, 0.10);
  --aha-error-tint: rgba(230, 0, 92, 0.10);
}

html, body, #app { height: 100%; }

.stage {
  position: fixed;
  inset: 0;
  display: grid;
  grid-template-columns: 240px 1fr;
  background: #F5F6F8;
  color: #1A1A2E;
  font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
  overflow: hidden;
}

.stage-rail {
  background: white;
  border-right: 1px solid #E5E7EB;
  display: flex; flex-direction: column;
  padding: 16px 12px; gap: 12px;
  min-height: 0;
}
.stage-add-slide {
  display: flex; align-items: center; justify-content: center; gap: 8px;
  padding: 12px;
  border: 1px dashed rgba(106, 30, 187, 0.4);
  background: rgba(106, 30, 187, 0.06);
  color: #6A1EBB;
  border-radius: 10px;
  font-family: inherit; font-size: 13px; font-weight: 600;
  cursor: pointer;
  transition: background 160ms var(--ease-out), border-color 160ms var(--ease-out), transform 160ms var(--ease-out);
  flex-shrink: 0;
}
.stage-add-slide:hover { background: rgba(106, 30, 187, 0.12); border-color: #6A1EBB; transform: translateY(-1px); }
.stage-add-slide:active { transform: scale(0.98); }
.stage-add-slide .material-symbols-rounded { font-size: 18px; }

.stage-thumbs {
  flex: 1; overflow-y: auto;
  display: flex; flex-direction: column; gap: 10px;
  padding-right: 4px;
  scrollbar-width: thin; scrollbar-color: rgba(15, 15, 20, 0.18) transparent;
}
.stage-thumbs::-webkit-scrollbar { width: 6px; }
.stage-thumbs::-webkit-scrollbar-thumb { background: rgba(15, 15, 20, 0.18); border-radius: 3px; }

.stage-slide-thumb {
  height: 80px;
  background: white;
  border: 1px solid #E5E7EB;
  border-radius: 10px;
  display: grid; place-items: center;
  font-size: 13px; font-weight: 600;
  color: rgba(15, 15, 20, 0.4);
  flex-shrink: 0;
}
.stage-slide-thumb--active {
  border-color: #6A1EBB;
  box-shadow: 0 0 0 3px rgba(106, 30, 187, 0.12);
  color: #6A1EBB;
}

.stage-main { display: grid; grid-template-rows: auto 1fr; min-width: 0; min-height: 0; }
.stage-topbar {
  height: 52px; display: flex; align-items: center; justify-content: space-between;
  padding: 0 24px; background: white; border-bottom: 1px solid #E5E7EB;
}
.stage-topbar-title { font-size: 13px; font-weight: 600; color: #1A1A2E; }
.stage-topbar-meta {
  font-size: 12px;
  color: rgba(15, 15, 20, 0.45);
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.stage-topbar-heart { font-size: 14px; color: var(--aha-pink); }

.stage-canvas {
  display: grid; place-items: center; padding: 32px;
  background: #F5F6F8;
}
.stage-empty {
  width: 100%; max-width: 960px; height: 100%;
  background: white; border-radius: 14px;
  border: 1px solid #E5E7EB;
  display: grid; place-items: center; gap: 8px;
  text-align: center; color: rgba(15, 15, 20, 0.45);
  padding: 32px;
  box-shadow: 0 1px 2px rgba(15, 15, 20, 0.02), 0 12px 32px -16px rgba(15, 15, 20, 0.1);
}
.stage-empty .material-symbols-rounded { font-size: 56px; color: rgba(15, 15, 20, 0.18); }
.stage-empty p { font-size: 14px; max-width: 36ch; margin: 0; line-height: 1.45; }
.stage-empty-hint { font-size: 12px; color: rgba(15, 15, 20, 0.35); }
.stage-empty strong { color: #6A1EBB; }
.stage-empty em { color: #1A1A2E; font-style: normal; font-weight: 600; }

.overlay-backdrop {
  position: fixed; inset: 0;
  background: rgba(15, 15, 20, 0.18);
  z-index: 100;
  transition: background 320ms var(--ease-drawer), backdrop-filter 320ms var(--ease-drawer);
}
.overlay-backdrop.--strong {
  background: rgba(15, 15, 20, 0.45);
  backdrop-filter: blur(2px);
}
.backdrop-enter-active, .backdrop-leave-active { transition: opacity 200ms var(--ease-out); }
.backdrop-enter-from, .backdrop-enter, .backdrop-leave-to { opacity: 0; }

/* ===========================================================
   Dialog with shared search header. Width morphs between
   picker (464) and marketplace (880); the inner header stays
   put so the search input grows/shrinks in place.
   =========================================================== */
.dialog {
  position: fixed; z-index: 101;
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: 8px;
  border: 1px solid #D4D4D4;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  transition:
    width 380ms var(--ease-drawer),
    height 380ms var(--ease-drawer),
    left 380ms var(--ease-drawer),
    top 380ms var(--ease-drawer),
    box-shadow 380ms var(--ease-drawer),
    border-radius 380ms var(--ease-drawer),
    border-color 380ms var(--ease-drawer);
}
.dialog.--expanded {
  border-radius: 18px;
  border-color: rgba(15, 15, 20, 0.1);
  box-shadow:
    0 1px 2px rgba(15, 15, 20, 0.04),
    0 28px 72px -18px rgba(15, 15, 20, 0.45);
}
.dialog.--mobile {
  border-radius: 0;
  border: 0;
  box-shadow: none;
  transition: none;
}

.dialog-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border-bottom: 1px solid rgba(15, 15, 20, 0.06);
  background: white;
  flex-shrink: 0;
}
.dialog-back {
  display: inline-flex; align-items: center;
  padding: 7px 12px;
  border: 1px solid;
  background: white;
  font-family: inherit; font-size: 13px; font-weight: 600;
  border-radius: 999px;
  cursor: pointer;
  flex-shrink: 0;
  overflow: hidden;
  white-space: nowrap;
  max-width: 200px;
  /* Collapse/expand rides the dialog's 380ms timing so the pill morphs
     into / out of the search bar as one continuous motion. */
  transition:
    max-width 380ms var(--ease-drawer),
    padding 380ms var(--ease-drawer),
    margin-right 380ms var(--ease-drawer),
    border-width 380ms var(--ease-drawer),
    opacity 280ms var(--ease-out),
    background 160ms var(--ease-out),
    border-color 160ms var(--ease-out),
    color 160ms var(--ease-out);
}
.dialog-back .material-symbols-rounded {
  font-size: 16px;
  transition: color 160ms var(--ease-out);
}
.dialog-back-inner {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.dialog-back-content-enter-active, .dialog-back-content-leave-active {
  transition: opacity 160ms var(--ease-out), transform 200ms var(--ease-drawer);
}
.dialog-back-content-enter-from, .dialog-back-content-enter,
.dialog-back-content-leave-to {
  opacity: 0;
  transform: translateX(-4px);
}
/* Pops back to marketplace home from a slide's detail page. */
.dialog-back.--market {
  color: rgba(15, 15, 20, 0.7);
  border-color: rgba(15, 15, 20, 0.14);
}
.dialog-back.--market .material-symbols-rounded { color: rgba(15, 15, 20, 0.6); }
.dialog-back.--market:hover {
  background: rgba(15, 15, 20, 0.04);
  color: #1A1A2E;
  border-color: rgba(15, 15, 20, 0.25);
}
.dialog-back.--market:hover .material-symbols-rounded { color: #1A1A2E; }

.dialog-back.--hidden {
  max-width: 0;
  padding-left: 0;
  padding-right: 0;
  margin-right: -10px;
  border-width: 0;
  opacity: 0;
  pointer-events: none;
}

.dialog-search {
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
  cursor: text;
  transition: background 200ms var(--ease-out);
}
.dialog-search.--readonly {
  /* Hints to the user that this bar will *open* the marketplace if
     they click — same DOM, different mode. */
  cursor: pointer;
}
.dialog-search-icon {
  position: absolute;
  left: 12px;
  font-size: 20px;
  color: rgba(15, 15, 20, 0.4);
  pointer-events: none;
}
.dialog-search-input {
  width: 100%;
  border: 0;
  outline: 0;
  background: rgba(15, 15, 20, 0.04);
  border-radius: 10px;
  padding: 9px 36px 9px 40px;
  font-size: 14px;
  font-family: inherit;
  color: #1A1A2E;
  transition: background 160ms var(--ease-out), box-shadow 160ms var(--ease-out);
}
.dialog-search-input::placeholder { color: rgba(15, 15, 20, 0.4); }
.dialog-search-input:focus {
  background: white;
  box-shadow: 0 0 0 3px rgba(106, 30, 187, 0.18);
}
.dialog-search.--readonly .dialog-search-input { cursor: pointer; }
.dialog-search-clear {
  position: absolute;
  right: 8px;
  width: 24px;
  height: 24px;
  display: grid;
  place-items: center;
  border: 0;
  background: transparent;
  border-radius: 6px;
  cursor: pointer;
  color: rgba(15, 15, 20, 0.45);
}
.dialog-search-clear:hover { background: rgba(15, 15, 20, 0.08); color: #1A1A2E; }
.dialog-search-clear .material-symbols-rounded { font-size: 18px; }

/* ===========================================================
   Segmented mode tabs (My Bookmarks · Marketplace).
   The two pills share a soft pill background so they read as
   one segmented control; the active pill lifts into white.
   =========================================================== */
.dialog-modes {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 3px;
  border-radius: 999px;
  background: rgba(15, 15, 20, 0.06);
  flex-shrink: 0;
}
.dialog-mode {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 0;
  background: transparent;
  border-radius: 999px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  color: rgba(15, 15, 20, 0.62);
  cursor: pointer;
  white-space: nowrap;
  transition:
    background 200ms var(--ease-out),
    color 160ms var(--ease-out),
    box-shadow 200ms var(--ease-out);
}
.dialog-mode .material-symbols-rounded {
  font-size: 16px;
  transition: color 160ms var(--ease-out);
}
.dialog-mode:hover {
  color: #1A1A2E;
}
.dialog-mode:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px rgba(106, 30, 187, 0.35);
}
.dialog-mode.--active {
  background: white;
  color: var(--aha-purple);
  box-shadow:
    0 1px 0 rgba(15, 15, 20, 0.04),
    0 4px 10px -4px rgba(15, 15, 20, 0.15);
}
.dialog-mode.--active .material-symbols-rounded { color: var(--aha-purple); }
/* Bookmarks tab adopts the pink brand colour when it's the active
   surface — keeps the personal-list signalling consistent. */
[data-testid="dialog-mode-bookmarks"].--active {
  color: var(--aha-pink);
}
[data-testid="dialog-mode-bookmarks"].--active .material-symbols-rounded {
  color: var(--aha-pink);
}
.dialog-mode-count {
  display: inline-grid;
  place-items: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: rgba(15, 15, 20, 0.08);
  color: rgba(15, 15, 20, 0.7);
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.02em;
  line-height: 1;
}
.dialog-mode.--active .dialog-mode-count {
  background: rgba(255, 64, 129, 0.14);
  color: var(--aha-pink);
}

.dialog-close {
  width: 36px; height: 36px;
  border: 0; background: transparent;
  border-radius: 10px;
  display: grid; place-items: center;
  color: rgba(15, 15, 20, 0.55);
  cursor: pointer;
  transition: background 160ms var(--ease-out), color 160ms var(--ease-out);
  flex-shrink: 0;
  /* Pin to the right edge in both modes — without this the bookmarks
     surface (which hides the flex:1 search bar) would let the close
     button drift in alongside the segmented tabs. */
  margin-left: auto;
}
.dialog-close:hover { background: rgba(15, 15, 20, 0.06); color: #1A1A2E; }
.dialog-close .material-symbols-rounded { font-size: 22px; }

.dialog-body {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}
.dialog-pane {
  width: 100%;
  height: 100%;
  overflow: hidden;
}

/* Confirm dialog — unchanged from before. */
.confirm-overlay {
  position: fixed; inset: 0; z-index: 200;
  background: rgba(15, 15, 20, 0.45);
  backdrop-filter: blur(2px);
  display: grid; place-items: center;
  padding: 24px;
}
.confirm-dialog {
  width: min(380px, 100%);
  background: white;
  border-radius: 14px;
  padding: 22px 22px 18px;
  box-shadow:
    0 1px 2px rgba(15, 15, 20, 0.06),
    0 24px 64px -12px rgba(15, 15, 20, 0.4);
  display: grid;
  gap: 8px;
  text-align: center;
}
.confirm-icon {
  width: 44px; height: 44px;
  margin: 0 auto 4px;
  display: grid; place-items: center;
  border-radius: 999px;
  background: rgba(230, 0, 92, 0.08);
  color: #E6005C;
  font-size: 22px;
}
.confirm-title { font-size: 16px; font-weight: 700; color: var(--aha-text); line-height: 1.3; margin: 0; }
.confirm-body { font-size: 13px; line-height: 1.5; color: rgba(15, 15, 20, 0.6); margin: 0 0 8px; }
.confirm-actions { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-top: 4px; }
.confirm-btn {
  padding: 10px 14px;
  border-radius: 10px;
  font-family: inherit; font-size: 13px; font-weight: 600;
  cursor: pointer;
  transition: background 160ms var(--ease-out), color 160ms var(--ease-out), border-color 160ms var(--ease-out);
}
.confirm-btn--ghost { background: white; color: rgba(15, 15, 20, 0.7); border: 1px solid rgba(15, 15, 20, 0.14); }
.confirm-btn--ghost:hover { background: rgba(15, 15, 20, 0.04); color: #1A1A2E; }
.confirm-btn--danger { background: #E6005C; color: white; border: 1px solid transparent; }
.confirm-btn--danger:hover { background: #C40050; }
.confirm-btn--danger:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(230, 0, 92, 0.3); }

.confirm-enter-active { transition: opacity 160ms var(--ease-out); }
.confirm-leave-active { transition: opacity 140ms var(--ease-out); }
.confirm-enter-active .confirm-dialog, .confirm-leave-active .confirm-dialog {
  transition: transform 220ms var(--ease-drawer), opacity 180ms var(--ease-out);
}
.confirm-enter-from, .confirm-enter, .confirm-leave-to { opacity: 0; }
.confirm-enter-from .confirm-dialog, .confirm-enter .confirm-dialog { opacity: 0; transform: translateY(8px) scale(0.96); }
.confirm-leave-to .confirm-dialog { opacity: 0; transform: translateY(4px) scale(0.98); }

.toast {
  position: fixed; bottom: 24px; left: 50%;
  transform: translateX(-50%);
  background: #1A1A2E; color: white;
  padding: 10px 16px; border-radius: 999px;
  font-size: 13px; font-weight: 500;
  display: inline-flex; align-items: center; gap: 8px;
  z-index: 200;
  box-shadow: 0 8px 24px -6px rgba(0, 0, 0, 0.5);
}
.toast .material-symbols-rounded { font-size: 18px; }
.toast strong { font-weight: 700; }
.toast-enter-active { transition: opacity 220ms var(--ease-out), transform 220ms var(--ease-drawer); }
.toast-leave-active { transition: opacity 160ms var(--ease-out), transform 160ms var(--ease-out); }
.toast-enter-from, .toast-enter { opacity: 0; transform: translateX(-50%) translateY(24px); }
.toast-leave-to { opacity: 0; transform: translateX(-50%) translateY(8px); }

@media (prefers-reduced-motion: reduce) {
  .stage-add-slide, .stage-add-slide:hover, .stage-add-slide:active { transform: none; }
  .dialog { transition-property: opacity; }
  .overlay-backdrop { transition: none; }
}
</style>
